import { Constructor } from '@angular/cdk/table';
import { FormGroup } from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DigitalPublicationDataService } from 'src/app/content-management/shared/services/digital-publication-data.service';
import { DialogService } from '../services/dialog.service';
import {
  AutoCompleteData,
  IAutoCompleteData,
} from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';

type WithPlatformFormSectionType = {
  isSearchingPlatforms: boolean;
  platformsSearchResult: IAutoCompleteData[];
  platformExactMatch: IAutoCompleteData;
  _initPlatform(platform: string): void;
  _initPlatformValueChanges(getPreviousPlatform: () => string | null): void;
  _buildPlatform(): string;
  onAddNewPlatform(text: string);
};

type PlatformFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: { PLATFORM: () => string };
  destroy$: Subject<boolean>;
  digitalPublicationDataService: DigitalPublicationDataService;
  _dialogService: DialogService;
};

export function WithPlatformFormSection<
  TBase extends Constructor<PlatformFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithPlatformFormSectionType;
  prototype: WithPlatformFormSectionType;
} & TBase {
  return class WithPlatformFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    isSearchingPlatforms: boolean = false;
    platformsSearchResult: IAutoCompleteData[];
    platformExactMatch: IAutoCompleteData;

    _initPlatform(platform: string): void {
      this.form
        .get(this.FORM_PATHS.PLATFORM())
        .setValue(new AutoCompleteData(platform, platform));

      this.form
        .get(this.FORM_PATHS.PLATFORM())
        .valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(500),
          tap(() => {
            this.isSearchingPlatforms = true;
          }),
          switchMap((searchedValue: string) => {
            const getDataObs$ =
              searchedValue && typeof searchedValue === 'string'
                ? this.digitalPublicationDataService.searchPlatforms$(
                    searchedValue
                  )
                : of(null);

            return getDataObs$.pipe(
              finalize(() => {
                this.isSearchingPlatforms = false;
              })
            );
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.platformsSearchResult = response.results.map((p) => {
                return new AutoCompleteData(p.text, p.text);
              });
              if (response.exactMatch) {
                this.platformExactMatch = new AutoCompleteData(
                  response.exactMatch.text,
                  response.exactMatch.text
                );
              } else {
                this.platformExactMatch = null;
              }
            } else {
              this.platformsSearchResult = null;
              this.platformExactMatch = null;
            }
          },
        });
    }

    _initPlatformValueChanges(getPreviousPlatform: () => string | null): void {
      this.form
        .get(this.FORM_PATHS.PLATFORM())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue.displayName === getPreviousPlatform()) {
              this.form.get(this.FORM_PATHS.PLATFORM()).markAsPristine();
            }
          },
        });
    }

    _buildPlatform(): string {
      return this.form.get(this.FORM_PATHS.PLATFORM()).value.displayName;
    }

    onAddNewPlatform(text: string) {
      return this.digitalPublicationDataService
        .createPlatform$(text)
        .subscribe({
          next: (_) =>
            this.form
              .get(this.FORM_PATHS.PLATFORM())
              .setValue(new AutoCompleteData(text, text)),
        });
    }
  };
}
